import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import {
  FixedTimeRangeSelectionService,
  ThemeService,
  ESelectedNavigationView,
} from '@wind-log_library/fe-component-library';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProcessDetailScheduleService } from '../../../shared/services-api-v2/kpi-follow-up/general/process-detail-schedule.service';
import { KpiAnalyticsModel } from '../../../shared/services-api-v2/kpi-follow-up/models/kpi-analytics.model';
import { ProcessesAnalyticsService } from '../../../shared/services-api-v2/planning/process-analytics/process-analytics.service';
import { ProcessEventsAndTClassBlocksModel } from 'app/shared/services-api-v2/kpi-follow-up/models/process-events-and-t-class-blocks.model';
import { BlockService } from 'app/shared/services/block-service/block.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { capitalizeFirstLetter } from 'app/shared/helper/is-number.helper';
import { InitialDataService } from '../../../shared/services/data-services/initial-data/initial-data.service';
import { CdkPortal } from '@angular/cdk/portal';
import { LayoutService } from 'app/shared/services/layout/layout.service';
import { CustomNavigationTitleService } from 'app/shared/services/custom-nav-title/custom-nav-title.service';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import moment from 'moment';

@Component({
  selector: 'app-process-details',
  templateUrl: './process-details.component.html',
  styleUrls: ['./process-details.component.scss', '../../../../assets/styles/kpi-styles/kpi-table-styles.scss'],
  standalone: false,
})
export class ProcessDetailsComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() selectedProcess;
  @Input() parkId?;
  @Input() isProcessUnassigning: boolean;
  @Output() backToList = new EventEmitter();
  @Output() optionSelectedEvents = new EventEmitter();
  @Output() optionSelectedBlocks = new EventEmitter();
  @ViewChild(CdkPortal, { static: false }) private portal: CdkPortal;

  public eventsAndTClassBlocks;
  public loading;
  public selectedIndex = 0;
  public legend = [
    { name: 'Maintenance', color: this.themeService.getColor('--event-category-maintainance') },
    { name: 'Repairs', color: this.themeService.getColor('--event-category-repairs') },
    { name: 'Grid Limitation', color: this.themeService.getColor('--event-category-grid-lim') },
    { name: 'Environmental', color: this.themeService.getColor('--event-category-environmental') },
    { name: 'Inspection', color: this.themeService.getColor('--event-category-inspection') },
  ];
  public scheduleFullScreen: boolean;
  public timeRange: BehaviorSubject<{ startTS: number; endTS: number }> = new BehaviorSubject<{
    startTS: number;
    endTS: number;
  }>({ startTS: null, endTS: null });
  public plannedEventsIds: string[];
  public tClassBlocksIds: string[];
  public processStartEnd: number[];

  private unsubscriber = new ReplaySubject();
  private firstAvTs: Date;

  constructor(
    private fixedTimeRangeSelectionService: FixedTimeRangeSelectionService,
    private processesAnalyticsService: ProcessesAnalyticsService,
    private blockService: BlockService,
    public themeService: ThemeService,
    private translateService: TranslateService,
    private initialDataService: InitialDataService,
    public processDetailScheduleService: ProcessDetailScheduleService,
    private customnavTitleService: CustomNavigationTitleService,
    private timezoneService: TimezoneService,
    private layoutService: LayoutService,
  ) {}

  public ngOnInit() {
    this.initialDataService.getFirstAvailableDataAsDate().subscribe(firstAvTs => {
      this.firstAvTs = firstAvTs;
      this.fixedTimeRangeSelectionService.generateTimesTampAndOptions(
        this.firstAvTs.getFullYear(),
        this.firstAvTs.getMonth() as any,
        this.translateService.currentLang,
      );
    });

    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      if (event) {
        this.fixedTimeRangeSelectionService.generateTimesTampAndOptions(
          this.firstAvTs.getFullYear(),
          this.firstAvTs.getMonth() as any,
          event.lang,
        );
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes?.selectedProcess?.currentValue) {
      this.plannedEventsIds = (changes.selectedProcess.currentValue as KpiAnalyticsModel).plannedEventsIds;
      this.tClassBlocksIds = (changes.selectedProcess.currentValue as KpiAnalyticsModel).tClassBlocksIds;
      this.processStartEnd = [
        (changes.selectedProcess.currentValue as KpiAnalyticsModel).estimatedStart,
        (changes.selectedProcess.currentValue as KpiAnalyticsModel).estimatedEnd,
      ];
      this.getStartDate();
      this.subscribeOnTimeRangeChanges();
    }
  }

  public ngOnDestroy() {
    this.layoutService.setHeader(null);
    this.customnavTitleService.clearTitle();
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public backToAll() {
    this.backToList.emit();
  }

  public optionSelectedEventsFunc(option: any) {
    this.optionSelectedEvents.emit(option);
  }

  public optionSelectedBlocksFunc(option: any) {
    this.optionSelectedBlocks.emit(option);
  }

  private getStartDate() {
    if (this.parkId) {
      this.processesAnalyticsService
        .processAnalyticsStartDatesPark(this.selectedProcess.id, this.parkId)
        .subscribe(data => this.setStartDate(data));
    } else {
      this.processesAnalyticsService
        .processAnalyticsStartDates(this.selectedProcess.id)
        .subscribe(data => this.setStartDate(data));
    }
  }

  private setStartDate(data) {
    if (data.length) {
      const min = Math.min(...data.map(item => item.startTs));
      const max = Math.max(...data.map(item => item.endTs));
      this.fixedTimeRangeSelectionService.onCustomRangeSelected({ startTS: min, endTS: max });
      this.fixedTimeRangeSelectionService.setSelectedNavigationView(ESelectedNavigationView.CUSTOM);
    }
  }

  private getBlocksAndEvents(start: number, end: number) {
    const { id } = this.selectedProcess;
    if (this.parkId) {
      this.processesAnalyticsService
        .processAnalyticsParkData(start, end, id, this.parkId)
        .subscribe(data => this.blockaAndEvents(JSON.parse(JSON.stringify(data)), start, end));
    } else {
      this.processesAnalyticsService
        .processAnalyticsPortfolioData(start, end, id)
        .subscribe(data => this.blockaAndEvents(JSON.parse(JSON.stringify(data)), start, end));
    }
  }

  private blockaAndEvents(data: any, start: number, end: number) {
    this.eventsAndTClassBlocks = this.parseEventsAndBlocks(data, start, end);
    this.selectedProcess.durationPlanned =
      this.eventsAndTClassBlocks.plannedEvents && this.eventsAndTClassBlocks.plannedEvents.length
        ? this.eventsAndTClassBlocks.plannedEvents.reduce((acc, val) => ({ duration: acc.duration + val.duration }))
            .duration
        : 0;
    this.selectedProcess.durationRealized =
      this.eventsAndTClassBlocks.tClassBlocks && this.eventsAndTClassBlocks.tClassBlocks.length
        ? this.eventsAndTClassBlocks.tClassBlocks.reduce((acc, val) => ({ duration: acc.duration + val.duration }))
            .duration
        : 0;
    this.selectedProcess.durationDeviation =
      this.selectedProcess.durationRealized - this.selectedProcess.durationPlanned;
    this.selectedProcess.productionLossRealized =
      this.eventsAndTClassBlocks.tClassBlocks && this.eventsAndTClassBlocks.tClassBlocks.length
        ? this.eventsAndTClassBlocks.tClassBlocks.reduce((acc, val) => ({
            totalProductionLoss: acc.totalProductionLoss + val.totalProductionLoss,
          })).totalProductionLoss
        : 0;
    this.selectedProcess.revenueLossRealized =
      this.eventsAndTClassBlocks.tClassBlocks && this.eventsAndTClassBlocks.tClassBlocks.length
        ? this.eventsAndTClassBlocks.tClassBlocks.reduce((acc, val) => ({
            totalRevenueLoss: acc.totalRevenueLoss + val.totalRevenueLoss,
          })).totalRevenueLoss
        : 0;
    this.loading = false;
  }

  private subscribeOnTimeRangeChanges(): void {
    combineLatest([
      this.fixedTimeRangeSelectionService.getSelectedTimesTamp(),
      this.timezoneService.getTimezoneObservable(),
    ])
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(([value]) => {
        this.loading = true;
        const start = this.timezoneService.applyTzToDate(moment(value.startTS).startOf('day').valueOf()).valueOf();
        const end = this.timezoneService.applyTzToDate(moment(value.endTS).endOf('day').valueOf()).valueOf();
        this.timeRange.next({ startTS: start, endTS: end });
        this.getBlocksAndEvents(start, end);
      });
  }

  private parseEventsAndBlocks(data, start, end) {
    const plannedEvents = data.DATA.TABLE.DATA_PLN.planned_events
      .filter(event => event.startTs < end && event.endTs > start)
      .map(event => {
        const currentPark = this.parkId ? data : data.PARKS.find(park => park.id === event.parkId);
        const currentTurbine = currentPark.TURBINES.find(t => t.id === event.turbineId);
        return {
          category: event.category,
          creationTs: event.creationTs,
          description: event.description,
          duration: event.duration,
          durationShort: event.durationShort,
          endTs: event.endTs,
          id: event.id,
          parkId: event.parkId,
          parkName: currentPark ? capitalizeFirstLetter(currentPark.name) : '-',
          productionLoss: event.productionLoss,
          revenueLoss: event.revenueLoss,
          startTs: event.startTs,
          title: event.title,
          turbineId: event.turbineId,
          turbineName: currentTurbine ? currentTurbine.code.replaceAll('_', ' ') : '-',
          turbineSerial: currentTurbine.serialNumeric,
        };
      });
    data.DATA.TABLE.DATA_LGB.logbook_monthly = this.groupByBlock(data.DATA.TABLE.DATA_LGB.logbook_monthly);
    const tClassBlocks = data.DATA.TABLE.DATA_LGB.logbook_monthly
      .sort((a, b) => a.startTs - b.startTs)
      .filter(logbook => logbook.startTs < end && logbook.endTs > start)
      .map(logbook => {
        const currentPark = this.parkId ? data : data.PARKS.find(park => park.id === logbook.parkId);
        const currentTurbine = currentPark.TURBINES.find(t => t.id === logbook.turbineId);
        return {
          category: logbook.category,
          tcu: ProcessEventsAndTClassBlocksModel.getTClassData(logbook.tcu, this.blockService),
          duration: logbook.duration,
          endTS: logbook.endTs,
          mutTS: logbook.mutTS,
          id: logbook.id,
          log: logbook.log,
          parkId: logbook.parkId,
          parkName: currentPark ? capitalizeFirstLetter(currentPark.name) : '-',
          totalProductionLoss: logbook.totalProductionLoss,
          totalRevenueLoss: logbook.totalRevenueLoss,
          startTS: logbook.startTs,
          turbineId: logbook.turbineId,
          turbineName: currentTurbine ? currentTurbine.code.replaceAll('_', ' ') : '-',
          turbineSerial: currentTurbine.serialNumeric,
        };
      });
    return { plannedEvents, tClassBlocks };
  }

  private groupByBlock(data) {
    const groupedBlocksById = [];
    const blocksIDs = [...new Set(data.map(item => item.id))];
    for (const id of blocksIDs) {
      const blocks = data.filter(b => b.id === id).sort((a, b) => a.startTs - b.startTs);
      const resultantBlock = JSON.parse(JSON.stringify(blocks[0]));
      resultantBlock.duration = 0;
      resultantBlock.totalProductionLoss = 0;
      resultantBlock.totalRevenueLoss = 0;
      blocks.forEach(b => {
        resultantBlock.duration += b.duration;
        resultantBlock.totalProductionLoss += b.totalProductionLoss;
        resultantBlock.totalRevenueLoss += b.totalRevenueLoss;
      });
      resultantBlock.endTs = blocks[blocks.length - 1].endTs;
      groupedBlocksById.push(resultantBlock);
    }
    return groupedBlocksById;
  }

  ngAfterViewInit(): void {
    this.layoutService.setHeader(this.portal);
  }
}
